/**
 * Two-level cache for immutable tile and issuer bytes: a bounded in-memory LRU, plus the Cache
 * Storage API when available (persists across reloads; silently absent on file:// or in tests).
 */
export class TileCache {
  private mem = new Map<string, Uint8Array>();
  private cachePromise: Promise<Cache | undefined> | undefined;

  constructor(private readonly opts: { maxEntries?: number; cacheName?: string } = {}) {}

  private get max(): number {
    return this.opts.maxEntries ?? 512;
  }

  private storage(): Promise<Cache | undefined> {
    if (!this.cachePromise) {
      this.cachePromise = (async () => {
        try {
          if (typeof caches === 'undefined') return undefined;
          return await caches.open(this.opts.cacheName ?? 'merklens-tiles-v1');
        } catch {
          return undefined;
        }
      })();
    }
    return this.cachePromise;
  }

  private static url(key: string): string {
    return 'https://merklens.invalid/' + encodeURIComponent(key);
  }

  async get(key: string): Promise<Uint8Array | undefined> {
    const m = this.mem.get(key);
    if (m) {
      this.mem.delete(key);
      this.mem.set(key, m); // refresh LRU position
      return m;
    }
    const c = await this.storage();
    if (!c) return undefined;
    try {
      const res = await c.match(TileCache.url(key));
      if (!res) return undefined;
      const bytes = new Uint8Array(await res.arrayBuffer());
      this.remember(key, bytes);
      return bytes;
    } catch {
      return undefined;
    }
  }

  /** `persistent` tiles (full tiles, issuers) also go to Cache Storage; partial tiles stay in memory. */
  async set(key: string, bytes: Uint8Array, persistent: boolean): Promise<void> {
    this.remember(key, bytes);
    if (!persistent) return;
    const c = await this.storage();
    if (!c) return;
    try {
      await c.put(TileCache.url(key), new Response(bytes as BodyInit, { headers: { 'content-type': 'application/octet-stream' } }));
    } catch {
      /* quota exceeded etc. */
    }
  }

  private remember(key: string, bytes: Uint8Array): void {
    this.mem.delete(key);
    this.mem.set(key, bytes);
    while (this.mem.size > this.max) {
      const oldest = this.mem.keys().next().value;
      if (oldest === undefined) break;
      this.mem.delete(oldest);
    }
  }

  async clear(): Promise<void> {
    this.mem.clear();
    try {
      if (typeof caches !== 'undefined') await caches.delete(this.opts.cacheName ?? 'merklens-tiles-v1');
    } catch {
      /* ignore */
    }
    this.cachePromise = undefined;
  }
}
